import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    SourceActivityCreatePayload,
    SourceActivityUpdatePayload,
    ISourceActivityDetail,
} from "@/customTypings/reference/SourceActivity";
import { SOURCE_ACTIVITY_CREATE_CONFLICT } from "@/customTypings/reference/SourceActivity";
import type { ReferenceRestoreDeletedPayload } from "@/utils/reference/referenceCreateConflict";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";
import { postReferenceCreate } from "@/utils/reference/referenceCreateConflict";

export const sourceActivitiesService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`/api/referencedata/source-activities/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<ISourceActivityDetail>;
}

function create(payload: SourceActivityCreatePayload) {
    return postReferenceCreate<ISourceActivityDetail>(
        `/api/referencedata/source-activities/create`,
        payload,
        SOURCE_ACTIVITY_CREATE_CONFLICT.deletedRestoreAvailable
    );
}

function restoreDeletedWithCreateValues(payload: ReferenceRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(
        `/api/referencedata/source-activities/restore-deleted-with-create-values`,
        requestOptions
    ).then(handleResponse) as Promise<ISourceActivityDetail>;
}

function update(payload: SourceActivityUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/source-activities/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<ISourceActivityDetail>>;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/referencedata/source-activities/${action}?id=${encodeURIComponent(id)}`,
        requestOptions
    ).then(handleResponse);
}
